import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
    CheckCircleIcon,
    ClockIcon,
    Cog6ToothIcon,
    EnvelopeIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    EyeSlashIcon,
    IdentificationIcon,
    KeyIcon,
    LockClosedIcon,
    PhoneIcon,
    ShieldCheckIcon,
    UserCircleIcon,
    XCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { PermissionSummary } from "@/features/admin-management/ui/PermissionCatalogEditor";
import { useAuth } from "@/features/auth/state/useAuth";
import { extractApiError } from "@/lib/api-error";

import { changeOwnPassword, getCurrentAdminProfile } from "../api/settingsApi";

const formatDate = (value: string | null | undefined) => {
    if (!value) return "—";
    return new Date(value).toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const StatusBadge = ({ status }: { status: string }) => {
    const styles = {
        ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        SUSPENDED: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        DEACTIVATED: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    };

    return (
        <span
            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                styles[status as keyof typeof styles] || styles.DEACTIVATED
            }`}
        >
            {status}
        </span>
    );
};

const InfoRow = ({
    icon: Icon,
    label,
    value,
}: {
    icon: React.ElementType;
    label: string;
    value: React.ReactNode;
}) => (
    <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800">
            <Icon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</p>
            <div className="mt-0.5 text-sm font-medium text-slate-900 dark:text-white">
                {value}
            </div>
        </div>
    </div>
);

export const SettingsRoute = () => {
    const { user } = useAuth();
    const [showPasswords, setShowPasswords] = useState(false);
    const [flashMessage, setFlashMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [formData, setFormData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });
    const [errors, setErrors] = useState<{
        currentPassword?: string;
        newPassword?: string;
        confirmPassword?: string;
    }>({});

    const profileQuery = useQuery({
        queryKey: ["current-admin-profile"],
        queryFn: getCurrentAdminProfile,
    });

    useEffect(() => {
        if (!flashMessage) return;
        const timer = window.setTimeout(() => setFlashMessage(null), 5000);
        return () => window.clearTimeout(timer);
    }, [flashMessage]);

    const resetPasswordForm = () => {
        setFormData({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
        });
        setErrors({});
        setShowPasswords(false);
    };

    const changePasswordMutation = useMutation({
        mutationFn: () =>
            changeOwnPassword({
                currentPassword: formData.currentPassword,
                newPassword: formData.newPassword,
            }),
        onSuccess: (response) => {
            resetPasswordForm();
            setFlashMessage({
                type: "success",
                text: response.message || "Password changed successfully.",
            });
        },
        onError: (error) => {
            setFlashMessage({
                type: "error",
                text: extractApiError(error),
            });
        },
    });

    const profile = profileQuery.data?.data;

    const displayRole = useMemo(() => {
        const role = profile?.role || user?.role;
        if (!role) return "—";
        return role.replace(/_/g, " ");
    }, [profile?.role, user?.role]);

    const validatePasswordForm = () => {
        const nextErrors: typeof errors = {};

        if (!formData.currentPassword) {
            nextErrors.currentPassword = "Current password is required.";
        }

        if (!formData.newPassword) {
            nextErrors.newPassword = "New password is required.";
        } else if (formData.newPassword.length < 8) {
            nextErrors.newPassword = "New password must be at least 8 characters.";
        }

        if (!formData.confirmPassword) {
            nextErrors.confirmPassword = "Please confirm the new password.";
        } else if (formData.confirmPassword !== formData.newPassword) {
            nextErrors.confirmPassword = "Passwords do not match.";
        }

        if (
            formData.currentPassword &&
            formData.newPassword &&
            formData.currentPassword === formData.newPassword
        ) {
            nextErrors.newPassword = "New password must be different from the current password.";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handlePasswordSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (validatePasswordForm()) {
            changePasswordMutation.mutate();
        }
    };

    if (profileQuery.isLoading && !profile) {
        return (
            <div className="flex h-96 items-center justify-center">
                <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent" />
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Loading account settings...
                    </p>
                </div>
            </div>
        );
    }

    if ((profileQuery.error || !profile) && !profileQuery.isLoading) {
        return (
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Settings</h1>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Review your account profile and security settings.
                    </p>
                </div>

                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                    <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                    <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">
                        {extractApiError(profileQuery.error)}
                    </p>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => profileQuery.refetch()}
                        className="mt-4"
                    >
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    const profileData = profile as NonNullable<typeof profile>;

    return (
        <div className="space-y-6">
            {flashMessage ? (
                <div
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-medium ${
                        flashMessage.type === "success"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400"
                            : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400"
                    }`}
                >
                    <div className="flex items-center gap-2">
                        {flashMessage.type === "success" ? (
                            <CheckCircleIcon className="h-4 w-4 shrink-0" />
                        ) : (
                            <XCircleIcon className="h-4 w-4 shrink-0" />
                        )}
                        <span>{flashMessage.text}</span>
                    </div>
                    <button
                        onClick={() => setFlashMessage(null)}
                        className="ml-4 rounded p-0.5 opacity-60 transition hover:opacity-100"
                        aria-label="Dismiss"
                    >
                        <XCircleIcon className="h-4 w-4" />
                    </button>
                </div>
            ) : null}

            <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Settings</h1>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Review your account profile and manage your password.
                </p>
            </div>

            <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="border-b border-slate-200 bg-slate-50 px-6 py-5 dark:border-slate-800 dark:bg-slate-800/50">
                        <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 dark:bg-primary-900/30">
                                <UserCircleIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                                    Account Profile
                                </h2>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    Your current account information and access details.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid gap-6 p-6 md:grid-cols-2">
                        <InfoRow
                            icon={IdentificationIcon}
                            label="Admin ID"
                            value={<code className="rounded bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800">{profileData.adminId}</code>}
                        />
                        <InfoRow
                            icon={EnvelopeIcon}
                            label="Email Address"
                            value={
                                <span className="inline-flex items-center gap-2">
                                    {profileData.email}
                                    {profileData.emailVerified ? (
                                        <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                                    ) : null}
                                </span>
                            }
                        />
                        <InfoRow
                            icon={UserCircleIcon}
                            label="Full Name"
                            value={profileData.fullName}
                        />
                        <InfoRow
                            icon={PhoneIcon}
                            label="Phone Number"
                            value={profileData.phoneNumber || "—"}
                        />
                        <InfoRow
                            icon={ShieldCheckIcon}
                            label="Role"
                            value={displayRole}
                        />
                        <div className="md:col-span-2">
                            <InfoRow
                                icon={Cog6ToothIcon}
                                label="Explicit Permissions"
                                value={
                                    <PermissionSummary
                                        permissions={profileData.permissions}
                                        emptyLabel="No explicit permissions assigned"
                                        maxVisible={10}
                                    />
                                }
                            />
                        </div>
                        <div className="md:col-span-2">
                            <InfoRow
                                icon={ShieldCheckIcon}
                                label="Effective Permissions"
                                value={
                                    <PermissionSummary
                                        permissions={profileData.effectivePermissions}
                                        emptyLabel="No effective permissions returned"
                                        maxVisible={10}
                                    />
                                }
                            />
                        </div>
                        <InfoRow
                            icon={CheckCircleIcon}
                            label="Account Status"
                            value={<StatusBadge status={profileData.status} />}
                        />
                        <InfoRow
                            icon={EnvelopeIcon}
                            label="Email Verification"
                            value={profileData.emailVerified ? "Verified" : "Pending"}
                        />
                        <InfoRow
                            icon={ClockIcon}
                            label="Created At"
                            value={formatDate(profileData.createdAt)}
                        />
                        <InfoRow
                            icon={ClockIcon}
                            label="Last Updated"
                            value={formatDate(profileData.updatedAt)}
                        />
                    </div>
                </section>

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="border-b border-slate-200 bg-slate-50 px-6 py-5 dark:border-slate-800 dark:bg-slate-800/50">
                        <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-900/30">
                                <LockClosedIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                                    Change Password
                                </h2>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    Update your sign-in password securely.
                                </p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handlePasswordSubmit} className="space-y-5 p-6">
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                            Your current password is required. The backend currently enforces a
                            minimum length of 8 characters for the new password.
                        </div>

                        {[
                            {
                                key: "currentPassword",
                                label: "Current Password",
                                placeholder: "Enter your current password",
                            },
                            {
                                key: "newPassword",
                                label: "New Password",
                                placeholder: "Enter a new password",
                            },
                            {
                                key: "confirmPassword",
                                label: "Confirm New Password",
                                placeholder: "Re-enter the new password",
                            },
                        ].map(({ key, label, placeholder }) => (
                            <div key={key}>
                                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    {label}
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPasswords ? "text" : "password"}
                                        value={formData[key as keyof typeof formData]}
                                        onChange={(event) => {
                                            setFormData((previous) => ({
                                                ...previous,
                                                [key]: event.target.value,
                                            }));
                                            if (errors[key as keyof typeof errors]) {
                                                setErrors((previous) => ({
                                                    ...previous,
                                                    [key]: undefined,
                                                }));
                                            }
                                        }}
                                        placeholder={placeholder}
                                        className={`w-full rounded-xl border px-4 py-3 pr-11 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-950 dark:text-slate-100 ${
                                            errors[key as keyof typeof errors]
                                                ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                                : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                        }`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPasswords((previous) => !previous)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600 dark:hover:text-slate-200"
                                        aria-label={showPasswords ? "Hide passwords" : "Show passwords"}
                                    >
                                        {showPasswords ? (
                                            <EyeSlashIcon className="h-5 w-5" />
                                        ) : (
                                            <EyeIcon className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>
                                {errors[key as keyof typeof errors] ? (
                                    <p className="mt-1.5 text-sm text-rose-600 dark:text-rose-400">
                                        {errors[key as keyof typeof errors]}
                                    </p>
                                ) : null}
                            </div>
                        ))}

                        <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 pt-5 dark:border-slate-800">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={resetPasswordForm}
                                disabled={changePasswordMutation.isPending}
                            >
                                Reset Form
                            </Button>
                            <Button type="submit" disabled={changePasswordMutation.isPending}>
                                <KeyIcon className="mr-2 h-4 w-4" />
                                {changePasswordMutation.isPending
                                    ? "Changing Password..."
                                    : "Change Password"}
                            </Button>
                        </div>
                    </form>
                </section>
            </div>
        </div>
    );
};
